/** Localisation for NestPlot — no framework.
 *
 *  The call site is `t("key", {vars})`, i18next's shape, so moving to a
 *  library later would be mechanical rather than a rewrite. A catalogue of
 *  plain objects tree-shakes, prerenders, and ships inside the Capacitor
 *  builds with no runtime setup.
 *
 *  WHAT THIS IS NOT. The UI language is not the operator's COUNTRY. A UK
 *  operator runs their office in English and still bills in GBP under UK
 *  GDPR; a US operator with a Spanish-speaking renter base wants the tenant
 *  portal in Spanish with US dollars. Those are separate axes and conflating
 *  them gets both wrong. Country lives on the operator (data/countries.py);
 *  language lives here.
 */

// English is imported statically: it is the fallback, so it must be present
// synchronously on the very first render, and it is the locale most sessions
// use. The others are code-split — bundling three catalogues of chrome into
// every first paint is weight nobody asked for on a tenant's phone.
import { EN } from "./messages/en";

export type Catalogue = Record<string, string>;

/** The three the product speaks today.
 *
 *  ORDER is display order in the picker: English first (the fallback), then
 *  Spanish — which is first among the rest for a reason that has nothing to
 *  do with Spain. A large share of US renters are Spanish-speaking, and the
 *  surfaces they live in (the public listing, the reserve/checkout flow, the
 *  tenant portal) are exactly where English is a daily friction. That locale
 *  pays for itself with zero international expansion. French is next because
 *  Quebec's Charter of the French Language requires French consumer-facing
 *  documents (see data/countries.py CA notes).
 *
 *  de/it/pt/nl are deliberately NOT here. The machinery handles them — no
 *  layout assumes Latin text metrics and nothing concatenates sentence
 *  fragments — but a catalogue is inventory, and we do not stock a market we
 *  have no route into. See docs/internationalization.md.
 */
export const UI_LOCALES = ["en", "es", "fr"] as const;
export type Locale = (typeof UI_LOCALES)[number];

/** Endonyms: a language names itself in its own words. A picker that says
 *  "Spanish" to somebody who only reads Spanish has failed at its one job. */
export const LOCALE_ENDONYMS: Record<Locale, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
};

const STORE_KEY = "nestplot.locale";

export function isLocale(v: unknown): v is Locale {
  return typeof v === "string" && (UI_LOCALES as readonly string[]).includes(v);
}

/** The person's own explicit choice, if they have made one. */
export function storedLocale(): Locale | null {
  try {
    const v = localStorage.getItem(STORE_KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null; // private mode, blocked storage — fall through to detection
  }
}

export function rememberLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORE_KEY, locale);
  } catch {
    /* the choice just won't survive a reload */
  }
}

export function browserLocale(): Locale | null {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  const tags = nav?.languages?.length ? nav.languages : nav ? [nav.language] : [];
  for (const tag of tags) {
    const base = (tag || "").split("-")[0].toLowerCase();
    if (isLocale(base)) return base;
  }
  return null;
}

/** Explicit choice → the person's account → THE OPERATOR → browser → English.
 *
 *  The operator rung is the one a single-user tool does not have and NestPlot
 *  needs: an operator is a shared workplace. Its `locale` default lands on
 *  every staff seat, and an individual still overrides it for their own
 *  browser. Country is deliberately NOT consulted — plenty of operators in a
 *  non-English country want the app in English, and the operator's own
 *  `locale` already defaults FROM its country once, at the API, where it can
 *  be corrected rather than re-derived on every render here.
 */
export function resolveLocale(opts?: { userLocale?: string | null; orgLocale?: string | null }): Locale {
  const stored = storedLocale();
  if (stored) return stored;
  if (isLocale(opts?.userLocale)) return opts!.userLocale as Locale;
  if (isLocale(opts?.orgLocale)) return opts!.orgLocale as Locale;
  return browserLocale() ?? "en";
}

const LOADERS: Record<Exclude<Locale, "en">, () => Promise<{ [k: string]: unknown }>> = {
  es: () => import("./messages/es"),
  fr: () => import("./messages/fr"),
};

const CATALOGUES: Partial<Record<Locale, Catalogue>> = { en: EN };
const inFlight = new Map<Locale, Promise<void>>();

/** Fetch a locale's catalogue. Until it lands, `translate` answers in English
 *  — the right behaviour for a chunk still in the air: a readable page in the
 *  wrong language beats a blank one in the right language. A failed load is
 *  swallowed on purpose (one network hiccup → English text; throwing would
 *  take the page down). */
export async function loadCatalogue(locale: Locale): Promise<void> {
  if (CATALOGUES[locale]) return;
  let p = inFlight.get(locale);
  if (!p) {
    const loader = locale === "en" ? null : LOADERS[locale];
    if (!loader) return;
    p = loader()
      .then((mod) => {
        const found = Object.values(mod).find(
          (v) => v && typeof v === "object" && !Array.isArray(v),
        ) as Catalogue | undefined;
        if (found) CATALOGUES[locale] = found;
      })
      .catch(() => {
        /* stay on English */
      })
      .finally(() => inFlight.delete(locale));
    inFlight.set(locale, p);
  }
  return p;
}

export function isLoaded(locale: Locale): boolean {
  return !!CATALOGUES[locale];
}

/** Plural categories, by hand and only for what we ship.
 *
 *  A key may carry `|` variants selected by the `count` var:
 *      "one unit | {count} units"
 *
 *  All three current locales have two forms. French is the one that differs
 *  in a way that shows: it treats 0 as singular ("0 logement"), where English
 *  does not ("0 units"). */
function pluralIndex(locale: Locale, n: number): number {
  const abs = Math.abs(n);
  if (locale === "fr") return abs < 2 ? 0 : 1;
  return abs === 1 ? 0 : 1;
}

export function translate(locale: Locale, key: string, vars?: Record<string, unknown>): string {
  let raw = CATALOGUES[locale]?.[key] ?? EN[key] ?? key;
  if (raw.includes("|") && vars && typeof vars.count === "number") {
    const forms = raw.split("|").map((s) => s.trim());
    raw = forms[Math.min(pluralIndex(locale, vars.count), forms.length - 1)] ?? forms[0];
  }
  if (!vars) return raw;
  // An unsupplied variable is left visible as `{name}` rather than rendered as
  // "undefined": a missing value should look like a bug to whoever reads it,
  // not like a sentence that happens to be nonsense.
  return raw.replace(/\{(\w+)\}/g, (m, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : m,
  );
}
